// Templates (plan §5.1 `tests.template`: card | banner | inline).
//
// XSS resistance is structural, not filtered: every piece of text goes in via
// `textContent`, every node via `createElement`. There is no HTML string
// anywhere in this file (enforced by the no-restricted-properties ESLint rule),
// so a hostile `headline` of `<img src=x onerror=...>` becomes literal text and
// nothing else. URLs are re-parsed with the URL constructor and only http(s)
// survives, which is what makes `javascript:` click_urls inert.
import { log } from './config';
import type { Config, Offer } from './types';

const PREFIX = 'mdfs-';

// The stand-in for an absent `fields` / `style`, with no prototype.
//
// `{}` would not do, and this is the half of #75 the boundary copy in ./api
// cannot reach: `ownProperties` strips the prototype off everything the *body*
// contains, but an offer that simply has no `fields` never contributes an
// object at all — the `|| {}` below does, and a bare object literal inherits
// the host page's `Object.prototype` like any other. So `fields.headline` on a
// fields-less offer read straight off the pollution.
const EMPTY = Object.create(null) as Record<string, never>;

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

/** Class/attribute token hardening — nothing but [A-Za-z0-9_-] reaches the DOM. */
function token(v: unknown): string {
  return str(v).replace(/[^A-Za-z0-9_-]/g, '').slice(0, 32);
}

function el(tag: string, cls: string, text?: string): HTMLElement {
  const n = document.createElement(tag);
  n.className = PREFIX + cls;
  if (text) n.textContent = text;
  return n;
}

/**
 * Scheme-check a URL. Absolute URLs only — the API always returns absolute
 * click_urls and image_urls, and refusing to resolve relative strings against
 * the host page means a hostile field value can never be coerced into a link.
 *
 * - links: http(s) only — `javascript:`, `data:`, `vbscript:` and everything
 *   else return null and no anchor is rendered at all.
 * - images (`httpsOnly`): https **and** a listed hostname. Creative images are
 *   our own bucket-hosted assets (plan §14), so the allowlist is pinned rather
 *   than optional: an embed with no `data-mdfs-img-hosts` renders no creative
 *   image at all (the rest of the offer still renders). "Any https host" was
 *   the previous default and it shipped the client-side half of §14 switched
 *   off on every page that forgot the attribute (#49).
 *
 * The single entry `*` is how an embed asks for any https host **explicitly**
 * (#76). It exists because the server has that mode too: with
 * `creative.image_hosts` unset and `service.env` not production,
 * `mdfs.core.creative` serves image URLs on any https host, so a snapshot
 * legitimately carries them. Without a way to say so, the generated embed for
 * such a deployment said "no hosts", the snippet dropped every image, and a
 * development page rendered headline + CTA + disclosure with no creative and
 * nothing anywhere explaining it. This is a declaration in the markup, not a
 * default: an embed that omits the attribute still renders no image, so #49
 * stands and a production deployment is untouched. It widens the host check
 * and only that — https-only, absolute-only, no `javascript:` — and it is a
 * whole entry, not a glob: `*.example.com` matches nothing.
 */
export function safeUrl(
  raw: unknown,
  httpsOnly: boolean,
  hosts: string[] | null,
): string | null {
  const s = str(raw).trim();
  if (!s) return null;
  let u: URL;
  try {
    u = new URL(s);
  } catch {
    return null;
  }
  if (u.protocol !== 'https:' && (httpsOnly || u.protocol !== 'http:')) return null;
  if (httpsOnly && !(hosts && (hosts.indexOf('*') >= 0 || hosts.indexOf(u.hostname) >= 0))) {
    // Only reachable for images, and the one thing worth saying: the snapshot
    // has a URL this page refuses. Silence here is what made the mismatch
    // expensive to diagnose (#76) — `log` is already gated on
    // `data-mdfs-debug`, so a production page pays nothing for it.
    log('img host', u.hostname);
    return null;
  }
  return u.href;
}

/** Build one offer node. Never throws on malformed input — it renders less. */
function offerNode(offer: Offer, cfg: Config): HTMLElement {
  const fields = (offer && offer.fields) || EMPTY;
  const tplRaw = token(offer.template) || 'card';
  const tpl = tplRaw === 'banner' || tplRaw === 'inline' ? tplRaw : 'card';
  const inline = tpl === 'inline';

  const root = el(inline ? 'span' : 'div', 'offer ' + PREFIX + tpl);
  const style = (fields.style || EMPTY) as Record<string, unknown>;
  const scheme = token(style.color_scheme);
  const layout = token(style.layout);
  if (scheme) root.setAttribute('data-mdfs-scheme', scheme);
  if (layout) root.setAttribute('data-mdfs-layout', layout);

  const img = inline ? null : safeUrl(fields.image_url, true, cfg.imgHosts);
  if (img) {
    const i = document.createElement('img');
    i.className = PREFIX + 'img';
    i.src = img;
    i.alt = ''; // decorative: the headline carries the meaning
    i.setAttribute('loading', 'lazy');
    // The image origin has no business knowing which page carries the creative.
    // Set as an attribute, not via the `referrerPolicy` IDL property: the
    // property is unimplemented in jsdom, so the unit suite could not see it.
    i.setAttribute('referrerpolicy', 'no-referrer');
    root.appendChild(i);
  }

  const headline = str(fields.headline);
  if (headline) root.appendChild(el(inline ? 'span' : 'h3', 'headline', headline));

  const body = str(fields.body);
  if (body && tpl === 'card') root.appendChild(el('p', 'body', body));

  const label = str(fields.cta_label) || 'Learn more';
  const href = safeUrl(offer.click_url, false, null);
  if (href) {
    const a = document.createElement('a');
    a.className = PREFIX + 'cta';
    a.textContent = label;
    a.href = href;
    a.rel = 'sponsored noopener';
    if (cfg.target) a.target = cfg.target;
    root.appendChild(a);
  } else {
    // Unsafe/absent click_url: the creative still renders, but inert.
    log('unsafe url', offer.click_url);
    root.appendChild(el('span', 'cta', label));
  }

  const disclosure = str(offer.disclosure);
  if (disclosure) root.appendChild(el('small', 'disclosure', disclosure));

  return root;
}

/**
 * Render offers into the container.
 *
 * Built off-document in a fragment and swapped in one shot: a partial render is
 * impossible, and the container's reserved box is never touched (no CLS — we
 * only replace children, never set style/width/height on the container).
 *
 * **Clear on fill, preserve on failure** (#52). Whatever the host page put
 * inside the container is a service-down fallback (README → "Host markup as a
 * fallback"): it is removed the moment we have something to show, and left
 * exactly as it is when we do not — a timeout, a network error, a no-fill or an
 * offers array with nothing usable in it all return 0 and touch nothing. This
 * is the workaround for `default_offer` degrading to `collapse` when the
 * service is unreachable: `fallback_mode` lives only in the response, so a page
 * that cannot reach us can only fall back to what it already contains.
 */
export function renderOffers(container: Element, offers: Offer[], cfg: Config): number {
  const frag = document.createDocumentFragment();
  let n = 0;
  for (let i = 0; i < offers.length; i++) {
    const o = offers[i];
    if (o && typeof o === 'object') {
      frag.appendChild(offerNode(o, cfg));
      n++;
    }
  }
  if (n) {
    container.textContent = ''; // host fallback markup goes only once we can fill
    container.appendChild(frag);
  }
  return n;
}
